import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { supabase } from "../../supabaseClient"
import { Card, CardContent } from "../../components/ui/card"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"

interface School {
  id: number
  name: string
  abbreviation: string | null
}

interface ModalState {
  isOpen: boolean
  mode: 'add' | 'edit'
  school: School | null
}

export default function SchoolManager() {
  const [schools, setSchools] = useState<School[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [modal, setModal] = useState<ModalState>({ isOpen: false, mode: 'add', school: null })
  const [modalName, setModalName] = useState('')
  const [modalAbbrev, setModalAbbrev] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 25
  const navigate = useNavigate()

  useEffect(() => {
    checkAdminAndLoad()
  }, [])

  async function checkAdminAndLoad() {
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      navigate('/login')
      return
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', user.id)
      .single()

    if (!profile?.is_admin) {
      navigate('/dashboard')
      return
    }

    await loadSchools()
  }

  async function loadSchools() {
    const { data, error } = await supabase
      .from('team_schools')
      .select('id, name, abbreviation')
      .order('name', { ascending: true })

    if (error) {
      console.error('Error loading schools:', error)
    } else {
      setSchools(data || [])
    }
    setLoading(false)
  }

  function openAddModal() {
    setModal({ isOpen: true, mode: 'add', school: null })
    setModalName('')
    setModalAbbrev('')
    setError('')
  }

  function openEditModal(school: School) {
    setModal({ isOpen: true, mode: 'edit', school })
    setModalName(school.name)
    setModalAbbrev(school.abbreviation || '')
    setError('')
  }

  function closeModal() {
    setModal({ isOpen: false, mode: 'add', school: null })
    setModalName('')
    setModalAbbrev('')
    setError('')
  }

  async function handleSave() {
    if (!modalName.trim()) {
      setError('School name is required')
      return
    }

    setSaving(true)
    setError('')

    if (modal.mode === 'edit' && modal.school) {
      const { error } = await supabase
        .from('team_schools')
        .update({ 
          name: modalName.trim(),
          abbreviation: modalAbbrev.trim() || null
        })
        .eq('id', modal.school.id)

      if (error) {
        setError(error.message)
      } else {
        setSchools(schools.map(s => 
          s.id === modal.school!.id 
            ? { ...s, name: modalName.trim(), abbreviation: modalAbbrev.trim() || null }
            : s
        ))
        closeModal()
      }
    } else {
      const { data, error } = await supabase
        .from('team_schools')
        .insert({
          name: modalName.trim(),
          abbreviation: modalAbbrev.trim() || null
        })
        .select()
        .single()

      if (error) {
        setError(error.message)
      } else if (data) {
        setSchools([...schools, data].sort((a, b) => a.name.localeCompare(b.name)))
        closeModal()
      }
    }
    setSaving(false)
  }

  const filteredSchools = schools.filter(s => 
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.abbreviation && s.abbreviation.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  // Pagination
  const totalPages = Math.ceil(filteredSchools.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedSchools = filteredSchools.slice(startIndex, startIndex + itemsPerPage)

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery])

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh] text-gray-500">Loading...</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin" className="text-gray-400 hover:text-gray-600">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Schools Database</h1>
          <p className="text-gray-500">Manage the master list of team names</p>
        </div>
      </div>

      {/* Stats */}
      <div className="text-sm text-gray-500">
        {schools.length} schools total
      </div>

      {/* Search + Add */}
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <Input
            type="text"
            placeholder="Search schools..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.3-4.3"/>
          </svg>
        </div>
        <Button 
          onClick={openAddModal}
          className="bg-[#1e3a5f] hover:bg-[#002a6b] text-white"
        >
          + Add
        </Button>
      </div>

      {/* Modal for Add/Edit */}
      {modal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="bg-white w-full max-w-md rounded-xl shadow-2xl">
            <CardContent className="p-6">
              <h2 className="text-lg font-bold text-[#1e3a5f] mb-4">
                {modal.mode === 'edit' ? 'Edit School' : 'Add School'}
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">School Name</label>
                  <Input
                    placeholder="e.g., Kansas"
                    value={modalName}
                    onChange={(e) => setModalName(e.target.value)}
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Abbreviation (optional)</label>
                  <Input
                    placeholder="e.g., KU"
                    value={modalAbbrev}
                    onChange={(e) => setModalAbbrev(e.target.value.toUpperCase())}
                    maxLength={10}
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <Button 
                    onClick={closeModal}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSave}
                    disabled={saving || !modalName.trim()}
                    className="flex-1 bg-[#1e3a5f] hover:bg-[#002a6b] text-white"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Schools List */}
      <Card className="bg-white border-0 shadow-sm">
        <CardContent className="p-0">
          {/* Empty State */}
          {filteredSchools.length === 0 && (
            <div className="py-12 text-center text-gray-500">
              {searchQuery ? 'No schools match your search' : 'No schools yet'}
            </div>
          )}

          {/* Schools */}
          <div className="divide-y divide-gray-100">
            {paginatedSchools.map((school) => (
              <div 
                key={school.id}
                className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#1e3a5f] truncate">{school.name}</div>
                  {school.abbreviation && (
                    <div className="text-sm text-gray-500">{school.abbreviation}</div>
                  )}
                </div>
                <button
                  onClick={() => openEditModal(school)}
                  className="text-[#1e3a5f] hover:text-[#F15A22] text-sm font-medium px-3 py-1"
                >
                  Edit
                </button>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <span className="text-sm text-gray-500">
                {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredSchools.length)} of {filteredSchools.length}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
                >
                  Prev
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

